import {
  type InactiveSessionState,
  type ActiveSessionState,
  ManualInterventionSubStatuses,
  ErrorWorkflowStateStatus,
  ClosedWorkflowStateStatus,
  CompletedWorkflowStateStatus,
} from "@financeable/brokerloop-data-plane";
import { type LodgeAIAutomation } from "@/store/slices/types/lodgeAITypes";
import {
  isInactiveSessionState,
  isActiveSessionState,
  isManualInterventionWorkflowState,
} from "./sessionStateTypeGuards";

const workflowStatusAbstraction = (sessionState: InactiveSessionState | ActiveSessionState): LodgeAIAutomation => {
  const defaultState: LodgeAIAutomation = {
    ...sessionState,
    isManualIntervention: false,
    isCompleted: false,
    isClosed: false,
    isErrored: false,
  };

  // Inactive session state.
  if (isInactiveSessionState(sessionState)) {
    const { status } = sessionState.workflowState;

    // Workflow errored.
    if (status === ErrorWorkflowStateStatus.Error) {
      return {
        ...defaultState,
        isErrored: true,
      };
    }

    return {
      ...defaultState,
      isClosed: true,
    };
  }

  // Active session state.
  if (isActiveSessionState(sessionState)) {
    const { status } = sessionState.workflowState;

    // Workflow completed.
    if (status === CompletedWorkflowStateStatus.Completed) {
      return {
        ...defaultState,
        isCompleted: true,
      };
    }

    if (isManualInterventionWorkflowState(sessionState.workflowState)) {
      const { subStatus } = sessionState.workflowState;

      // Manual intervention due to action error triggered.
      if (subStatus === ManualInterventionSubStatuses.ActionErrorTriggered) {
        return {
          ...defaultState,
          isManualIntervention: true,
          manualInterventionReason: "Oops, we hit an error, please take over.",
          manualInterventionIntent: "danger",
        };
      }

      // Manual intervention due to validation error triggered.
      if (subStatus === ManualInterventionSubStatuses.ValidationErrorTrigerred) {
        return {
          ...defaultState,
          isManualIntervention: true,
          manualInterventionReason: "The value in the field is not as we expected, do you want to skip this step?",
          manualInterventionIntent: "danger",
        };
      }

      // Manual intervention due to user completion required.
      if (subStatus === ManualInterventionSubStatuses.UserCompletionRequired) {
        return {
          ...defaultState,
          isManualIntervention: true,
          manualInterventionReason: "We would need your help to complete this step.",
          manualInterventionIntent: "warning",
        };
      }

      // Else, it is most likely a manual intervention is due to user request.
      // So we do not need to show any reason or intent.
      return {
        ...defaultState,
        isManualIntervention: true,
      };
    }
  }

  // Other statuses.
  return defaultState;
};

export { workflowStatusAbstraction };
